/**
 * Normalize proof photos for completion upload.
 *
 * Mobile file inputs often report empty type or HEIC; GCS v4 signed URLs
 * require an exact Content-Type match on PUT. We always upload JPEG, like
 * the capture camera pipeline.
 */

const JPEG_MIME = 'image/jpeg';
const JPEG_QUALITY = 0.85;
/**
 * A basic Android phone reported "Devido à insuficiência de memória…" here —
 * Chrome's renderer OOM — while attaching a proof photo to a run step. The old
 * 2048 cap was not the problem on its own: the cost is that the camera original
 * gets decoded at FULL resolution before it is scaled, so a 12MP frame is ~48MB
 * of bitmap before the canvas even exists.
 *
 * Kept in step with PROOF_MIN_LONG_EDGE in the app's ProofCameraModal so the
 * same step photographed from the app and from this link carry the same detail.
 * The floor is set for reading — an expiry date or a thermometer display — not
 * for framing.
 */
const MAX_EDGE = 1920;

/**
 * Read pixel dimensions from a JPEG's header so the decode itself can be asked
 * for a smaller bitmap. Only the first chunk is fetched — this must not become
 * another full read of a large file.
 */
const HEADER_BYTES = 128 * 1024;

async function readJpegSize(file: File): Promise<{ width: number; height: number } | null> {
  try {
    const buffer = await file.slice(0, HEADER_BYTES).arrayBuffer();
    const view = new DataView(buffer);
    if (view.byteLength < 4 || view.getUint16(0) !== 0xffd8) return null;
    let offset = 2;
    while (offset + 9 < view.byteLength) {
      if (view.getUint8(offset) !== 0xff) {
        offset += 1;
        continue;
      }
      const marker = view.getUint8(offset + 1);
      // SOF0/1/2/3/5/6/7/9/10/11/13/14/15 carry the frame dimensions; DHT/DAC
      // and the restart markers in between never do.
      const isStartOfFrame = marker >= 0xc0 && marker <= 0xcf
        && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc;
      if (isStartOfFrame) {
        return {
          height: view.getUint16(offset + 5),
          width: view.getUint16(offset + 7),
        };
      }
      if (marker === 0xd8 || marker === 0xd9 || (marker >= 0xd0 && marker <= 0xd7)) {
        offset += 2;
        continue;
      }
      offset += 2 + view.getUint16(offset + 2);
    }
  } catch {
    // Unreadable header — fall back to a plain decode.
  }
  return null;
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Could not read this photo. Try another image.'));
    img.src = src;
  });
}

type DecodedPhoto = ImageBitmap | HTMLImageElement;

/**
 * Decode off the document's rendering pipeline when we can.
 *
 * Returning from the camera can leave that pipeline parked until the next
 * touch, and an `<img>` load never resolves while it is — the proof thumb
 * then only appears once the user taps. `createImageBitmap` does not wait on
 * it. EXIF orientation must be requested explicitly or phone photos land
 * sideways; the `<img>` fallback applies it on its own.
 */
async function decodePhoto(file: File): Promise<DecodedPhoto> {
  if (typeof createImageBitmap === 'function') {
    // Downsample DURING the decode when we know the source size. Only the long
    // edge is constrained so the browser keeps the aspect ratio itself — EXIF
    // rotation can transpose the header dimensions, and forcing both edges
    // would stretch those frames. The canvas pass still clamps the result.
    const size = await readJpegSize(file);
    if (size && size.width > 0 && size.height > 0) {
      const longest = Math.max(size.width, size.height);
      if (longest > MAX_EDGE) {
        const scale = MAX_EDGE / longest;
        const resize = size.width >= size.height
          ? { resizeWidth: Math.max(1, Math.round(size.width * scale)) }
          : { resizeHeight: Math.max(1, Math.round(size.height * scale)) };
        try {
          return await createImageBitmap(file, {
            imageOrientation: 'from-image',
            resizeQuality: 'high',
            ...resize,
          });
        } catch {
          // Older engines reject the resize options — try a plain decode.
        }
      }
    }
    try {
      return await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch {
      // HEIC and some encoders are rejected here — fall back to the DOM path.
    }
  }
  const objectUrl = URL.createObjectURL(file);
  try {
    return await loadImage(objectUrl);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function photoSize(source: DecodedPhoto): { width: number; height: number } {
  if (source instanceof HTMLImageElement) {
    return { width: source.naturalWidth || source.width, height: source.naturalHeight || source.height };
  }
  return { width: source.width, height: source.height };
}

function scaleToMaxEdge(width: number, height: number, maxEdge: number) {
  const longest = Math.max(width, height);
  if (longest <= maxEdge) return { width, height };
  const scale = maxEdge / longest;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

export async function normalizeProofPhoto(
  file: File,
): Promise<{ blob: Blob; contentType: typeof JPEG_MIME }> {
  const source = await decodePhoto(file);
  try {
    const size = photoSize(source);
    const { width, height } = scaleToMaxEdge(size.width, size.height, MAX_EDGE);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not prepare photo for upload.');
    ctx.drawImage(source, 0, 0, width, height);
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, JPEG_MIME, JPEG_QUALITY),
    );
    if (!blob) throw new Error('Could not process photo for upload.');
    return { blob, contentType: JPEG_MIME };
  } finally {
    if (!(source instanceof HTMLImageElement)) source.close();
  }
}

/**
 * PUT to a GCS v4 signed URL (XHR — clearer CORS errors than fetch on mobile).
 * Prefer uploadProofViaCallable for the execute PWA (no bucket CORS required).
 */
export async function uploadProofToSignedUrl(
  uploadUrl: string,
  blob: Blob,
  contentType: string,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', contentType);

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
        return;
      }
      reject(
        new Error(
          `Photo upload failed (HTTP ${xhr.status}). Try again without a photo or use a different image.`,
        ),
      );
    };
    xhr.onerror = () => {
      reject(
        new Error(
          'Photo upload was blocked by the browser. The storage bucket may need CORS for use.x9.app — try again later or complete without a photo.',
        ),
      );
    };
    xhr.ontimeout = () => reject(new Error('Photo upload timed out. Try again.'));
    xhr.timeout = 120_000;
    xhr.send(blob);
  });
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('Could not read photo data.'));
        return;
      }
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(new Error('Could not read photo data.'));
    reader.readAsDataURL(blob);
  });
}

/**
 * Upload a normalized proof blob through the Cloud Function (Admin SDK).
 * Avoids browser CORS on the Storage bucket.
 */
export async function proofBlobToBase64(blob: Blob): Promise<string> {
  return blobToBase64(blob);
}
